import { IMessage, IContentBlock, TRole, IToolResultBlock } from "@/lib/types";
import { v7 as uuidv7 } from "uuid";

export interface StreamChunk {
    messageStart?: {
        role: TRole;
    };
    contentBlockStart?: {
        contentBlockIndex: number;
        start: {
            toolUse?: {
                name: string;
                toolUseId?: string;
            };
        };
    };
    contentBlockDelta?: {
        contentBlockIndex: number;
        delta: {
            reasoningContent?: {
                text?: string;
                signature?: string;
            };
            toolUse?: {
                input: string;
            };
            text?: string;
        };
    };
    contentBlockStop?: {
        contentBlockIndex: number;
    };
    messageStop?: {
        stopReason: string;
    };
    streamError?: {
        message: string;
    };
    toolResult?: IToolResultBlock;
    metadata?: {
        usage?: {
            inputTokens: number;
            outputTokens: number;
            totalTokens: number;
        };
        metrics?: {
            latencyMs: number;
        };
        //eslint-disable-next-line @typescript-eslint/no-explicit-any
        trace?: any;
    };
    message?: {
        role: TRole;
        content: IContentBlock[];
        messageid: string;
    };
    subagentReasoning?: { agentName: string; reasoningText: string };
    subagentToolUse?: { agentName: string; toolName: string; toolInput?: any };
    subagentToolResult?: { agentName: string; status: string; contentPreview: string };
    subagentText?: { agentName: string; text: string };
    subagentAuthRequired?: { agentName: string; provider: string; authorizationUrl: string };
}

export async function* parseStreamToChunks(
    stream: ReadableStream<Uint8Array>,
    onHeartbeat?: () => void
): AsyncGenerator<StreamChunk, void, unknown> {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    // Per-line SSE handling shared by the read loop and the post-loop flush.
    // It is a generator because the body yields StreamChunks; callers delegate with `yield*`.
    // Closes over onHeartbeat only (no mutable capture).
    function* processLine(line: string): Generator<StreamChunk, void, unknown> {
        // Skip empty lines and SSE comments (heartbeat keepalives)
        if (!line.trim()) return;
        if (line.startsWith(":")) {
            onHeartbeat?.();
            return;
        }

        // Parse SSE format - lines should start with "data: "
        if (line.startsWith("data: ")) {
            const jsonStr = line.substring(6); // Remove "data: " prefix

            try {
                const parsed: unknown = JSON.parse(jsonStr);

                if (parsed && typeof parsed === "object") {
                    const obj = parsed as Record<string, unknown>;

                    // Delta events are wrapped in { event: { ... } }
                    if ("event" in obj && obj.event && typeof obj.event === "object") {
                        yield obj.event as StreamChunk;
                    } else if ("message" in obj) {
                        // Complete messages arrive at top level as { message: { ... } }
                        yield { message: (parsed as StreamChunk).message } as StreamChunk;
                    } else if ("error" in obj) {
                        // Backend error events:
                        //   { type: "error", error: { message: "..." } }
                        //   { error: "plain string message" }
                        const err = obj.error;
                        const message =
                            typeof err === "string"
                                ? err
                                : String((err as Record<string, unknown>)?.message || "Unknown error");
                        yield {
                            streamError: { message },
                        } as StreamChunk;
                    } else if ("subagent_reasoning" in obj) {
                        const sr = obj.subagent_reasoning as { agent_name: string; reasoning_text: string };
                        yield {
                            subagentReasoning: { agentName: sr.agent_name, reasoningText: sr.reasoning_text },
                        };
                    } else if ("subagent_tool_use" in obj) {
                        const st = obj.subagent_tool_use as {
                            agent_name: string;
                            tool_name: string;
                            tool_input?: any;
                        };
                        yield {
                            subagentToolUse: {
                                agentName: st.agent_name,
                                toolName: st.tool_name,
                                toolInput: st.tool_input,
                            },
                        };
                    } else if ("subagent_tool_result" in obj) {
                        const sr = obj.subagent_tool_result as {
                            agent_name: string;
                            status: string;
                            content_preview: string;
                        };
                        yield {
                            subagentToolResult: {
                                agentName: sr.agent_name,
                                status: sr.status,
                                contentPreview: sr.content_preview,
                            },
                        };
                    } else if ("subagent_text" in obj) {
                        const st = obj.subagent_text as { agent_name: string; text: string };
                        yield { subagentText: { agentName: st.agent_name, text: st.text } };
                    } else if ("subagent_auth_required" in obj) {
                        const sa = obj.subagent_auth_required as {
                            agent_name: string;
                            provider: string;
                            authorization_url: string;
                        };
                        yield {
                            subagentAuthRequired: {
                                agentName: sa.agent_name,
                                provider: sa.provider,
                                authorizationUrl: sa.authorization_url,
                            },
                        };
                    }
                    // Skip control events: init_event_loop, start, start_event_loop
                }
            } catch (ex) {
                console.error(`Error parsing SSE line: ${jsonStr}`, ex);
            }
        }
    }

    try {
        while (true) {
            const { value, done } = await reader.read();

            if (done) break;

            // Decode and add to buffer
            buffer += decoder.decode(value, { stream: true });

            // Process complete lines (SSE format uses \n\n but we'll process line by line)
            const lines = buffer.split("\n");
            buffer = lines.pop() || ""; // Keep incomplete line in buffer

            for (const line of lines) {
                yield* processLine(line);
            }
        }

        // BUG-008 fix: the read loop holds the trailing incomplete line in `buffer`;
        // on `done` it would otherwise be discarded. Flush any partial multi-byte
        // UTF-8 sequence the streaming decoder is holding, then process the leftover
        // line through the same path. Empty `buffer` (newline-terminated stream) is a no-op.
        buffer += decoder.decode();
        yield* processLine(buffer);
    } finally {
        try {
            reader.releaseLock();
        } catch {
            // Stream may already be errored/closed from abort
        }
    }
}

export function chunksToContentBlock(chunks: StreamChunk[]): IContentBlock {
    const contentBlock: IContentBlock = {};

    for (const chunk of chunks) {
        // Handle complete messages (toolResults arrive this way)
        if (
            chunk.message &&
            chunk.message.role === "user" &&
            chunk.message.content.some((block) => block.toolResult !== undefined)
        ) {
            return chunk.message.content.find((block) => block.toolResult !== undefined) || {};
        }

        if (chunk.contentBlockStart) {
            if (chunk.contentBlockStart.start.toolUse) {
                contentBlock.toolUse = {
                    name: chunk.contentBlockStart.start.toolUse.name,
                    toolUseId: chunk.contentBlockStart.start.toolUse.toolUseId || uuidv7(),
                    input: "",
                };
            }
        }

        if (chunk.contentBlockDelta) {
            const delta = chunk.contentBlockDelta.delta;

            if (delta.reasoningContent) {
                if (!contentBlock.reasoningContent) {
                    contentBlock.reasoningContent = { reasoningText: { text: "" } };
                }
                if (contentBlock.reasoningContent.reasoningText) {
                    if (delta.reasoningContent.text) {
                        contentBlock.reasoningContent.reasoningText.text += delta.reasoningContent.text;
                    }
                    if (delta.reasoningContent.signature) {
                        contentBlock.reasoningContent.reasoningText.signature = delta.reasoningContent.signature;
                    }
                }
            }

            if (delta.toolUse && contentBlock.toolUse) {
                contentBlock.toolUse.input += delta.toolUse.input;
            }

            if (delta.text) {
                contentBlock.text = (contentBlock.text || "") + delta.text;
            }
        }
    }

    return contentBlock;
}

export function streamChunksToMessage(chunks: StreamChunk[]): IMessage[] {
    const messages: IMessage[] = [];
    let currentMessage: IMessage | null = null;
    const contentBlocks = new Map<number, IContentBlock>();

    for (const chunk of chunks) {
        if (chunk.messageStart) {
            if (currentMessage && currentMessage.content.length > 0) {
                messages.push(currentMessage);
            }
            currentMessage = {
                messageid: uuidv7(),
                role: chunk.messageStart.role,
                content: [],
            };
            contentBlocks.clear();
        }

        if (chunk.contentBlockStart) {
            const index = chunk.contentBlockStart.contentBlockIndex;
            const block: IContentBlock = {};

            if (chunk.contentBlockStart.start.toolUse) {
                block.toolUse = {
                    name: chunk.contentBlockStart.start.toolUse.name,
                    toolUseId: chunk.contentBlockStart.start.toolUse.toolUseId || uuidv7(),
                    input: "",
                };
            }

            contentBlocks.set(index, block);
        }

        if (chunk.contentBlockDelta && currentMessage) {
            const index = chunk.contentBlockDelta.contentBlockIndex;

            // Get or create block for this index
            let block = contentBlocks.get(index);
            if (!block) {
                block = {};
                contentBlocks.set(index, block);
            }

            const delta = chunk.contentBlockDelta.delta;

            if (delta.reasoningContent) {
                if (!block.reasoningContent) {
                    block.reasoningContent = { reasoningText: { text: "" } };
                }
                if (block.reasoningContent.reasoningText) {
                    if (delta.reasoningContent.text) {
                        block.reasoningContent.reasoningText.text += delta.reasoningContent.text;
                    }
                    if (delta.reasoningContent.signature) {
                        block.reasoningContent.reasoningText.signature = delta.reasoningContent.signature;
                    }
                }
            }

            if (delta.toolUse && block.toolUse) {
                block.toolUse.input += delta.toolUse.input;
            }

            if (delta.text) {
                block.text = (block.text || "") + delta.text;
            }
        }

        if (chunk.contentBlockStop && currentMessage) {
            const index = chunk.contentBlockStop.contentBlockIndex;
            const block = contentBlocks.get(index);

            if (block && Object.keys(block).length > 0) {
                currentMessage.content.push(block);
            }
            contentBlocks.delete(index);
        }

        if (chunk.messageStop && currentMessage) {
            if (currentMessage.content.length > 0) {
                messages.push(currentMessage);
            }
            currentMessage = null;
            contentBlocks.clear();
        }

        // Handle complete messages (toolResults arrive this way)
        if (chunk.message) {
            // For user messages (toolResults), add them directly
            if (chunk.message.role === "user") {
                messages.push({
                    messageid: chunk.message.messageid,
                    role: chunk.message.role,
                    content: chunk.message.content,
                });
            }
            // For assistant messages, skip them - we're building from streaming events
            // (Complete assistant messages arrive after messageStop, but we've already
            // built the message from events. We can use these later for server IDs.)
        }
    }

    // Add any remaining message
    if (currentMessage && currentMessage.content.length > 0) {
        messages.push(currentMessage);
    }

    return messages;
}

// Gets a flattened list of content blocks from a message
export const getContentBlocks = (message: IMessage): IContentBlock[] => {
    return message.content.map((content) => ({ ...content, role: message.role }));
};

export const isUserToolError = (block: IContentBlock): boolean => {
    return block.toolResult !== undefined && block.toolResult.status === "error";
};

export const getUserToolErrorText = (block: IContentBlock): string => {
    return (
        block.toolResult?.content
            .filter((content) => content.text)
            .map((content) => content.text)
            .join(" ") || ""
    );
};

export const isUserWebTool = (block: IContentBlock): boolean => {
    return block.toolResult?.content.some((content) => content.json && content.json.search_parameters) || false;
};

export const getUserWebToolResults = (
    block: IContentBlock
): {
    title: string;
    snippet: string;
    link: string;
}[] => {
    // Do this for news results and image results
    if (block.toolResult?.content?.some((content) => content.json?.news_results)) {
        console.log(`found news results`);
        return block.toolResult?.content
            .flatMap((content) => content.json.news_results)
            .map((result) => {
                return {
                    title: result.title,
                    snippet: result.snippet,
                    link: result.link,
                };
            });
    } else {
        return (
            block.toolResult?.content
                .filter((content) => content.json && content.json.organic_results)
                .flatMap((content) => content.json.organic_results) || []
        );
    }
};

export const isUserTextTool = (block: IContentBlock): boolean => {
    return (
        (block.toolResult?.status === "success" &&
            block.toolResult?.content.some((content) => content.text !== undefined && content.text !== null)) ||
        false
    );
};

export const getUserTextToolResults = (block: IContentBlock): string => {
    return (
        block.toolResult?.content
            .filter((content) => content.text)
            .map((content) => content.text)
            .join(" ") || ""
    );
};

export const isUserImage = (block: IContentBlock): boolean => {
    return (
        block.role === "user" && block.image !== undefined && (block.projectfile === undefined || !block.projectfile)
    );
};

export const isUserDocument = (block: IContentBlock): boolean => {
    return (
        block.role === "user" && block.document !== undefined && (block.projectfile === undefined || !block.projectfile)
    );
};

export const isProjectFile = (block: IContentBlock): boolean => {
    return block.projectfile !== undefined && block.projectfile;
};

export const isUserDocumentTool = (block: IContentBlock): boolean => {
    return (
        block.toolResult?.status === "success" &&
        block.toolResult?.content.some((content) => content.json?.filename && content.json?.mime_type)
    );
};

export const getUserDocumentToolResults = (block: IContentBlock): { filename: string; key: string; url?: string }[] => {
    if (!block.toolResult?.content) return [];
    return block.toolResult.content
        .filter((content) => content.json?.filename && content.json?.key)
        .map((content) => ({
            filename: content.json.filename,
            key: content.json.key,
            ...(content.json.url ? { url: content.json.url } : {}),
        }));
};

export type OAuthProvider = "atlassian" | "github";

export const isOAuthAuthRequired = (block: IContentBlock): { url: string; provider: OAuthProvider } | null => {
    const jsonBlock = block.toolResult?.content?.find((c) => c.json !== undefined)?.json as
        | Record<string, unknown>
        | undefined;
    if (
        typeof jsonBlock?.type === "string" &&
        jsonBlock.type.endsWith("_auth_required") &&
        typeof jsonBlock.authorization_url === "string"
    ) {
        const provider = jsonBlock.type.replace("_auth_required", "") as OAuthProvider;
        if (provider === "atlassian" || provider === "github") {
            return { url: jsonBlock.authorization_url, provider };
        }
    }
    return null;
};

/** @deprecated Use isOAuthAuthRequired instead */
export const isAtlassianAuthRequired = (block: IContentBlock): { url: string } | null => {
    const result = isOAuthAuthRequired(block);
    if (result?.provider === "atlassian") return { url: result.url };
    return null;
};

export const isUserWebFetchTool = (block: IContentBlock): boolean => {
    if (!isUserTextTool(block)) return false;
    const text = getUserTextToolResults(block);
    return text.startsWith("Status Code:") && text.includes("Body:");
};

export const getUserWebFetchToolResults = (
    block: IContentBlock
): { statusCode: number; title: string; body: string } => {
    const text = getUserTextToolResults(block);
    const bodyMatch = text.match(/Body:\s*([\s\S]*)/);
    const statusMatch = text.match(/Status Code:\s*(\d+)/);
    const bodyContent = bodyMatch ? bodyMatch[1].trim() : text;
    const lines = bodyContent.split("\n");
    const title = lines[0]?.trim() || "Fetched web page";
    const body = lines.slice(1).join("\n").trim();
    return {
        statusCode: statusMatch ? parseInt(statusMatch[1]) : 200,
        title,
        body,
    };
};
